import React, { useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import {
    DossierDocumentsValidation,
    Layout,
    LAYOUT_DEFAULT,
    LAYOUT_DOCUMENTS_1_3,
    useDossierFormulaireContext,
    useDossierRoutingContext,
    useDossierValidationHeader,
    useSelectedDossierContext,
} from '@europrocurement/l2d-modules';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { useFullLayout } from '@europrocurement/flexy-components';
import DossierValidationBreadcrumb from '../DossierValidationBreadcrumb/DossierValidationBreadcrumb';
import DossierFormsValidation from '../DossierFormsValidation/DossierFormsValidation';

export type DossierInfoBodyValidationProps = {
    layout: Layout;
};

const DossierInfoBodyValidation: React.FunctionComponent<DossierInfoBodyValidationProps> =
    function (props) {
        const { layout } = props;
        const isDefaultLayout = [LAYOUT_DEFAULT, LAYOUT_DOCUMENTS_1_3].includes(layout);
        const { dossier } = useSelectedDossierContext();
        const navigate = useNavigate();
        const { setSidebarOpen } = useFullLayout();
        const { persistAllFormulaires, hasNonPersistedChanges } = useDossierFormulaireContext();
        const { setDossierValidationPath, setOnExit, pathOnExit } = useDossierValidationHeader();
        const { getDossierRoute } = useDossierRoutingContext();

        const handleChangeLayout = (layoutParam: Layout) => {
            // The url can already have different properties in the query, we just have to update only one property
            const urlParams = new URLSearchParams(document.location.search);
            urlParams.set('layout', layoutParam);
            window.open(
                `${document.location.origin + document.location.pathname}?${urlParams.toString()}`,
                '_blank',
            );
        };

        const onExit = useCallback(async () => {
            if (isDefaultLayout && hasNonPersistedChanges) {
                try {
                    await persistAllFormulaires();
                } catch (err) {
                    console.error(err);
                    enqueueSnackbar(
                        'Une erreur est survenue lors de la sauvegarde de votre dossier',
                        { variant: 'error' },
                    );

                    return;
                }
            }
            if (pathOnExit) {
                navigate(pathOnExit);
            } else {
                navigate(getDossierRoute(dossier.id as number));
            }
            enqueueSnackbar('Votre dossier a bien été sauvegardé');
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            dossier.id,
            getDossierRoute,
            hasNonPersistedChanges,
            isDefaultLayout,
            navigate,
            persistAllFormulaires,
        ]);

        const commonSx = {
            margin: '1em 0',
        };

        const defaultLayoutSx = {
            ...commonSx,
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
        };

        const documentLayoutSx = {
            ...commonSx,
        };

        useEffect(() => {
            setSidebarOpen(false);
            setDossierValidationPath(true);
            setOnExit(() => onExit);

            return () => {
                setSidebarOpen(true);
                setDossierValidationPath(false);
                setOnExit(() => {});
            };
        }, [setSidebarOpen, setDossierValidationPath, setOnExit, onExit]);

        if (!dossier.id) {
            return null;
        }

        return (
            <>
                <DossierValidationBreadcrumb isDefaultLayout={isDefaultLayout} />
                <Box sx={isDefaultLayout ? defaultLayoutSx : documentLayoutSx}>
                    <DossierDocumentsValidation
                        currentLayout={layout}
                        onLayoutChange={handleChangeLayout}
                    />
                    {isDefaultLayout && <DossierFormsValidation />}
                </Box>
            </>
        );
    };

export default DossierInfoBodyValidation;
